import React from "react";
import IRhapsodyCertificate from "src/ServerEntities/IRhapsodyCertificate";
import ErrorBox from 'src/SharedComponents/ErrorBox';
import LoadingIndicator from "src/SharedComponents/LoadingIndicator";
import { SessionContext } from "src/Views/SessionContext";
import { getRhapsodyCertificates, getRhapsodyHeader } from "../ramService";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { RolesContext } from "src/Roles/RolesContext";
import IRhapsodyServer from "src/ServerEntities/IRhapsodyServer";
import ServerSelector from "src/SharedComponents/Select/ServerSelector";
import useCookie from "src/CustomHooks/useCookie";
import { isQVera } from "./Queues";

const ContentContainer = styled.main`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  border: 4px solid #44A2D6;
  height: 100%;
  width: 80%;
  box-sizing: border-box;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
`;

const TableHeader = styled.thead`
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #1577AE;
  & svg {
    margin-left: 8px;
  }
  &.sort-asc svg {
    transform: rotate(180deg);
  }
`;

const TableRow = styled.tr`
  &:hover {
    background-color: #f2f2f2;
  }
`;

const TableCell = styled.td`
  padding: 6px 16px;
  color: #212529;
  font-weight: 600;
`;

const TitleBar = styled.div`
    margin: 8px;
    border-radius: 20px;
    background-color: #1577AE;
    padding: 5px;
    color: #FFF;
    text-align: center;
    font-size: 24px;
    font-weight: bold;
    box-sizing: border-box;
`;

const StatusBadge = styled.span<{ $status: 'Expired' | 'Expires within 1 month' | 'Active' | 'Does not expire'}>`
    padding: 4px 16px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    color: black;
    
    
    ${props => {
        switch (props.$status) {
            case 'Expired':
                return `
                    background-color: #FF2121;
                    color: #ffffff;
                `;
            case 'Expires within 1 month':
                return `
                    background-color: #2DF6C0;
                `;
            case 'Active':
                return `
                    background-color: #5CEA5A;
                `;
            case 'Does not expire':
                return `
                    background-color: #5CEA5A;
                `;
        }
    }}
`;

type SORT_COLUMNS = "expiry" | "alias" | "type";

const sortCertificates = (sortColumn: SORT_COLUMNS, sortAsc: boolean) => (a: IRhapsodyCertificate, b: IRhapsodyCertificate) => {
    if (sortColumn === "expiry") {
        if (sortAsc) {
            return a.expiry - b.expiry;
        }
        return b.expiry - a.expiry;
    }
    if (sortColumn === "alias") {
        if (sortAsc) {
            return a.alias.localeCompare(b.alias);
        }
        return b.alias.localeCompare(a.alias);
    }
    if (sortColumn === "type") {
        if (sortAsc) {
            return a.type.localeCompare(b.type);
        }
        return b.type.localeCompare(a.type);
    }
    return 0;
};

const MonitorCertificates = () => {
    const { state, dispatch } = React.useContext(SessionContext);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");
    const [certificates, setCertificates] = React.useState<IRhapsodyCertificate[]>([]);
    const [sortColumn, setSortColumn] = React.useState<SORT_COLUMNS>("expiry");
    const [sortAsc, setSortAsc] = React.useState(true);
    const [data, setData] = React.useState(undefined as unknown as IRhapsodyServer[]);
    const [selectedNamespace, setSelectedNamespace] = useCookie<string>('selectedNamespace', '');
    const selectedServer = state.selectedSever ?? 0;

    const setSelectedServer = (index: number) => {
        dispatch({ type: "setSelectedServer", payload: index });
    };
    const roleContext = React.useContext(RolesContext);
    const currentRole = roleContext.state.role;
    const headerInfo = state.headerInfo ? state.headerInfo : data as IRhapsodyServer[];
        
    React.useEffect(() => {
        setLoading(true);
        getRhapsodyHeader(state.webToken, (serverData: IRhapsodyServer[]) => {
            if (serverData[selectedServer]) {
                getRhapsodyCertificates(serverData[selectedServer].id, state.webToken, (data: IRhapsodyCertificate[]) => {
                    setCertificates(data);
                    setError("");
                    setLoading(false);
                }, (errorMessage: string) => {
                    setError(errorMessage);
                    setLoading(false);
                });
            } else {
                setLoading(false);
            }
            setData(serverData);
            dispatch({ type: "setHeaderInfo", payload: serverData });
        }, (errorMessage: string) => {
            setLoading(false);
            setError(errorMessage);
        }, isQVera(headerInfo, selectedServer) ? undefined : selectedNamespace);
    }, [currentRole, selectedNamespace]);

    const getExpiryStatus = (expiryDate: number) => {
        const now = Date.now();
        const oneMonth = 30 * 24 * 60 * 60 * 1000;
        if (expiryDate === 0) {
          return 'Does not expire';
        } else if (expiryDate < now) {
            return 'Expired';
        } else if (expiryDate < now + oneMonth) {
            return 'Expires within 1 month';
        }
        return 'Active';
    };

    const loadServers = () => {
        if (headerInfo && headerInfo[selectedServer]) {
            setLoading(true);
            getRhapsodyCertificates(headerInfo[selectedServer].id, state.webToken, (serverData: IRhapsodyCertificate[]) => {
                setLoading(false);
                setCertificates(serverData);
            }, (erroMessage: string) => {
                setLoading(false);
                setError(erroMessage);
            });
        }
    };

    React.useEffect(loadServers, [selectedServer]);

    const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const changeSortColumn = (newSortColumn: SORT_COLUMNS) => () => {
        if (newSortColumn === sortColumn) {
            setSortAsc(!sortAsc);
        } else {
            setSortColumn(newSortColumn);
            setSortAsc(true);
        }
    };

    return (
        <ContentContainer>
            <LoadingIndicator type="Linear" show={loading} />
            {error && <ErrorBox>{error}</ErrorBox>}
            {isQVera(headerInfo, selectedServer) ?
                <ServerSelector headerInfo={headerInfo} selectedServer={selectedServer} setSelectedServer={setSelectedServer} /> :
                <ServerSelector headerInfo={headerInfo} selectedNamespace={selectedNamespace} setSelectedNamespace={setSelectedNamespace} selectedServer={selectedServer} setSelectedServer={setSelectedServer} />
            }
            <TableContainer>
                <TitleBar>SSL Certificates</TitleBar>
                <StyledTable>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell className={(sortColumn === "alias" && sortAsc) ? "sort-asc" : "sort-desc"}>
                                Alias
                                <FontAwesomeIcon icon={faArrowUp} onClick={changeSortColumn("alias")} />
                            </TableHeaderCell>
                            <TableHeaderCell className={(sortColumn === "type" && sortAsc) ? "sort-asc" : "sort-desc"}>
                                Type
                                <FontAwesomeIcon icon={faArrowUp} onClick={changeSortColumn("type")} />
                            </TableHeaderCell>
                            <TableHeaderCell className={(sortColumn === "expiry" && sortAsc) ? "sort-asc" : "sort-desc"}>
                                Expiry
                                <FontAwesomeIcon icon={faArrowUp} onClick={changeSortColumn("expiry")}/>
                            </TableHeaderCell>
                            <TableHeaderCell />
                        </TableRow>
                    </TableHeader>
                    <tbody>
                        {certificates && certificates.sort(sortCertificates(sortColumn, sortAsc)).map((cert) => {
                            const status = getExpiryStatus(cert.expiry);
                            return (
                                <TableRow key={cert.id}>
                                    <TableCell>{cert.alias}</TableCell>
                                    <TableCell>{cert.type}</TableCell>
                                    <TableCell>{formatDate(cert.expiry)}</TableCell>
                                    <TableCell style={{display: "flex", justifyContent: "flex-end"}}>
                                        <StatusBadge $status={status}>
                                            {status}
                                        </StatusBadge>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </tbody>
                </StyledTable> 
            </TableContainer>
        </ContentContainer>
    );
};

export default MonitorCertificates;

